import { useEffect, useState } from "react";
import { submitSubjectSuggestion } from "../../services/subjectSuggestionsService";
import { showSystemMessage } from "../../utils/systemMessage";

const INITIAL_FORM = {
  name: "",
  category: "",
  gradeLevels: [],
  description: "",
};

const getValidationError = ({ name, category, gradeLevels }) =>
  [
    [!name.trim(), "Subject name is required."],
    [!category, "Category is required."],
    [gradeLevels.length === 0, "Select at least one education level."],
  ].find(([invalid]) => invalid)?.[1] ?? "";

export const SuggestSubjectModal = ({
  open,
  onClose,
  categories,
  gradeLevels,
}) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open) return null;

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    error && setError("");
  };

  const toggleGradeLevel = (levelName) => {
    setForm((current) => ({
      ...current,
      gradeLevels: current.gradeLevels.includes(levelName)
        ? current.gradeLevels.filter((level) => level !== levelName)
        : [...current.gradeLevels, levelName],
    }));
    error && setError("");
  };

  const closeModal = () => !submitting && (setError(""), onClose());

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;

    const validationError = getValidationError(form);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const response = await submitSubjectSuggestion({
        name: form.name.trim(),
        category: form.category,
        gradeLevels: form.gradeLevels,
        description: form.description.trim() || null,
      });

      showSystemMessage({
        message:
          response?.message ||
          "Your subject suggestion was submitted for review.",
        type: "success",
      });
      setForm(INITIAL_FORM);
      onClose();
    } catch (submitError) {
      setError(
        submitError?.message ||
          "Subject suggestion could not be submitted. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-3 sm:p-6">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="suggest-subject-title"
        className="flex max-h-[95dvh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl sm:max-h-[90vh]"
      >
        <div className="shrink-0 border-b px-4 py-4 sm:px-6">
          <div className="flex items-center justify-between gap-4">
            <h2
              id="suggest-subject-title"
              className="text-xl font-semibold text-gray-900 sm:text-2xl"
            >
              Suggest New Subject
            </h2>
            <button
              type="button"
              onClick={closeModal}
              disabled={submitting}
              aria-label="Close suggestion modal"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-2xl text-gray-500 hover:bg-gray-100 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
            >
              ×
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 space-y-5 overflow-y-auto px-4 py-5 sm:px-6">
            <input
              type="text"
              value={form.name}
              disabled={submitting}
              onChange={({ target }) => updateField("name", target.value)}
              placeholder="Name *"
              className="h-12 w-full rounded-lg border border-gray-300 px-4 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 disabled:cursor-not-allowed disabled:bg-gray-100"
            />

            <select
              value={form.category}
              disabled={submitting}
              onChange={({ target }) => updateField("category", target.value)}
              className="h-12 w-full rounded-lg border border-gray-300 bg-white px-4 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 disabled:cursor-not-allowed disabled:bg-gray-100"
            >
              <option value="">Category *</option>
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>

            <fieldset>
              <legend className="mb-3 text-sm font-medium text-gray-700">
                Education levels *
              </legend>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {gradeLevels.map(({ id, name }) => (
                  <label
                    key={id || name}
                    className="flex cursor-pointer items-center gap-2 text-sm text-gray-700"
                  >
                    <input
                      type="checkbox"
                      checked={form.gradeLevels.includes(name)}
                      disabled={submitting}
                      onChange={() => toggleGradeLevel(name)}
                      className="h-4 w-4 rounded border-gray-300 disabled:cursor-not-allowed disabled:opacity-50"
                    />
                    <span>{name}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            <textarea
              rows={5}
              value={form.description}
              disabled={submitting}
              onChange={({ target }) =>
                updateField("description", target.value)
              }
              placeholder="Description"
              className="min-h-32 w-full resize-y rounded-lg border border-gray-300 p-4 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 disabled:cursor-not-allowed disabled:bg-gray-100"
            />

            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>

          <div className="shrink-0 border-t bg-white px-4 py-4 sm:px-6">
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={closeModal}
                disabled={submitting}
                className="h-11 w-full rounded-lg border border-gray-300 px-6 text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="h-11 w-full rounded-lg bg-brand-medium-blue px-8 text-white hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
              >
                {submitting ? "Submitting..." : "Submit"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
